/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useCallback, memo } from 'react'
import DatePicker, {
  PATHS_WITH_YEAR_AUTOCOMPLETION
} from 'components/Input/Date'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import { Theme } from '@mui/material'

import useEmotionStyles from 'hooks/useEmotionStyles'
import * as CSSTypes from 'csstype'
import FieldDataCollector from '../FieldDataCollector'
import { PathFormFieldProps } from '../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../utils/tracking/amplitude/WithFieldInteraction'
import { useMeta } from '../../stores/utils/hooks/useMeta'
import { usePatchedField } from '../../stores/utils/hooks/usePatchedField'
import { convertFormat } from './helpers'

const DATE_FORMAT = 'yyyy-MM-dd'

const styles = (theme: Theme) => ({
  helperTextStyle: {
    color: theme.palette.grey[800],
    marginTop: 0,
    marginBottom: '8px',
    position: 'absolute' as CSSTypes.Property.Position,
    top: '62px',
    padding: '10px',
    boxShadow: theme.shadows[3],
    zIndex: 5,
    background: 'white',
    borderRadius: '5px'
  },
  container: {
    display: 'flex',
    flexDirection: 'column' as CSSTypes.Property.FlexDirection,
    position: 'relative' as CSSTypes.Property.Position
  }
})

const DateInput = observer(
  ({
    path,
    visualFormat = 'dd.MM.yyyy',
    onTooltip,
    addDay = true,
    onChange,
    onBlur,
    placeholder = 'TT.MM.JJJJ',
    outerLabel,
    smallTextField,
    showTooltipOnFieldClick,
    isTooltipHidden,
    labelProp,
    inputError,
    patch = true
  }: PathFormFieldProps) => {
    const isPathWithYearAutocompletion =
      PATHS_WITH_YEAR_AUTOCOMPLETION.includes(path)

    const fieldConfig = useMemo(
      () => ({
        format: (value: string | undefined) =>
          convertFormat(
            value || '',
            DATE_FORMAT,
            visualFormat,
            isPathWithYearAutocompletion
          ),
        parse: (value: string | undefined) => {
          const dateString = convertFormat(
            value || '',
            visualFormat,
            DATE_FORMAT,
            isPathWithYearAutocompletion
          )

          if (
            addDay &&
            dateString.length === visualFormat.length &&
            dateString.length < DATE_FORMAT.length
          ) {
            return `${dateString}-01`
          }

          return dateString
        }
      }),
      [visualFormat, addDay]
    )

    const { input, meta, error } = usePatchedField(
      path,
      fieldConfig,
      patch,
      isPathWithYearAutocompletion
        ? (value: string) => value.length === 10
        : undefined
    )

    const {
      traversal: { removeErrorsForPath }
    } = useStores()

    const changeHandler = useCallback(
      (_: unknown, raw?: string | null) => {
        input.onChange(raw)
        if (onChange) {
          onChange(raw as string)
        }
        removeErrorsForPath(path)
      },
      [input, removeErrorsForPath, path]
    )
    const blurHandler = useCallback(() => {
      input.onBlur()
      if (onBlur) {
        onBlur(input.value as string)
      }
    }, [input])
    const focusHandler = useCallback(() => {
      input.onFocus()
    }, [input])
    const { container } = useEmotionStyles(styles)

    const { label, tooltip } = useMeta(path)

    return (
      <WithFieldInteraction path={path}>
        <div className={container}>
          <DatePicker
            label={labelProp || label}
            tooltip={tooltip}
            name={input.name}
            value={input.value}
            outerLabel={outerLabel}
            error={inputError || (meta.touched ? error : '')}
            placeholder={placeholder}
            format={visualFormat}
            onChange={changeHandler}
            onBlur={blurHandler}
            onFocus={focusHandler}
            onTooltip={onTooltip}
            path={path}
            smallTextField={smallTextField}
            showTooltipOnFieldClick={showTooltipOnFieldClick}
            isTooltipHidden={isTooltipHidden}
          />
        </div>
      </WithFieldInteraction>
    )
  }
)

const PathDateInput = (props: PathFormFieldProps) => {
  const {
    path,
    labelPath,
    visualFormat,
    onTooltip,
    onChange,
    onBlur,
    placeholder,
    outerLabel,
    smallTextField,
    showTooltipOnFieldClick,
    isTooltipHidden,
    addDay,
    labelProp,
    inputError,
    patch
  } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <DateInput
        path={path}
        visualFormat={visualFormat}
        onTooltip={onTooltip}
        addDay={addDay}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        outerLabel={outerLabel}
        labelProp={labelProp}
        smallTextField={smallTextField}
        showTooltipOnFieldClick={showTooltipOnFieldClick}
        isTooltipHidden={isTooltipHidden}
        inputError={inputError}
        patch={patch}
      />
    </FieldDataCollector>
  )
}

export default memo(PathDateInput)
